(() => {
  const countCache = new Map();
  let observedGrid;

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return result >>> 0;
  };

  const seededShuffle = (items, seed) => {
    const shuffled = [...items];
    let state = seed || 1;
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      const target = state % (index + 1);
      [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
    }
    return shuffled;
  };

  const arrangeCards = (cards, seed, precedingCards = []) => {
    const groups = new Map();

    cards.forEach((card) => {
      const productId = card.dataset.productId;
      if (!groups.has(productId)) groups.set(productId, []);
      groups.get(productId).push(card);
    });

    if (groups.size < 2) return cards;

    const activeGroups = seededShuffle(
      [...groups.entries()].map(([productId, productCards]) => ({ productId, cards: productCards })),
      seed
    );
    const arrangedCards = [];
    let cursor = 0;

    while (activeGroups.some((group) => group.cards.length > 0)) {
      const completeOrder = precedingCards.concat(arrangedCards);
      const rowPartner = completeOrder.length % 2 === 1
        ? completeOrder[completeOrder.length - 1].dataset.productId
        : null;
      const previousProduct = completeOrder.length
        ? completeOrder[completeOrder.length - 1].dataset.productId
        : null;
      let selectedIndex = -1;

      for (let offset = 0; offset < activeGroups.length; offset += 1) {
        const index = (cursor + offset) % activeGroups.length;
        const group = activeGroups[index];
        if (!group.cards.length) continue;
        if (group.productId === rowPartner || group.productId === previousProduct) continue;
        selectedIndex = index;
        break;
      }

      if (selectedIndex === -1) {
        selectedIndex = activeGroups.findIndex((group) => group.cards.length > 0 && group.productId !== rowPartner);
      }
      if (selectedIndex === -1) {
        selectedIndex = activeGroups.findIndex((group) => group.cards.length > 0);
      }

      const selectedGroup = activeGroups[selectedIndex];
      arrangedCards.push(selectedGroup.cards.shift());
      cursor = (selectedIndex + 1) % activeGroups.length;
    }

    return arrangedCards;
  };

  const spreadCards = (grid) => {
    if (grid.dataset.variantCardsEnabled !== 'true') return;

    const cards = [...grid.querySelectorAll(':scope > [data-product-id]')];
    const placedCards = cards.filter((card) => card.dataset.variantCardPlaced === 'true');
    const newCards = cards.filter((card) => card.dataset.variantCardPlaced !== 'true');
    if (!newCards.length) return;

    const batchNumber = Number(grid.dataset.variantCardBatch || 0);
    const seed = hash(`${window.location.pathname}|${grid.dataset.currentPage || '1'}|${batchNumber}`);
    const arrangedNewCards = arrangeCards(newCards, seed, placedCards);

    arrangedNewCards.forEach((card) => {
      card.dataset.variantCardPlaced = 'true';
      grid.appendChild(card);
    });
    grid.dataset.variantCardBatch = String(batchNumber + 1);
  };

  const countCardsOnPage = (documentRoot) =>
    documentRoot.querySelectorAll('#product-grid[data-variant-cards-enabled="true"] > .flex-grid__item').length;

  const setDisplayedCount = (total) => {
    ['ProductCount', 'ProductCountMobile', 'ProductCountDesktop'].forEach((id) => {
      const element = document.getElementById(id);
      if (!element) return;
      element.textContent = `${total} ${total === 1 ? 'product' : 'products'}`;
      element.classList.remove('loading');
    });
  };

  const countAllVariantCards = async (grid) => {
    const totalPages = Number(grid.dataset.totalPages || 1);
    const currentPage = Number(grid.dataset.currentPage || 1);
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    const cacheKey = `${url.pathname}?${url.searchParams.toString()}|${totalPages}`;

    if (countCache.has(cacheKey)) {
      setDisplayedCount(countCache.get(cacheKey));
      return;
    }

    const requests = [];
    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        requests.push(Promise.resolve(countCardsOnPage(document)));
        continue;
      }

      const pageUrl = new URL(url);
      pageUrl.searchParams.set('page', page);
      requests.push(
        fetch(pageUrl.toString(), { credentials: 'same-origin' })
          .then((response) => response.text())
          .then((html) => countCardsOnPage(new DOMParser().parseFromString(html, 'text/html')))
      );
    }

    try {
      const counts = await Promise.all(requests);
      const total = counts.reduce((sum, count) => sum + count, 0);
      countCache.set(cacheKey, total);
      setDisplayedCount(total);
    } catch (error) {
      // Keep Shopify's standard count if a page request is interrupted.
    }
  };

  const initialise = () => {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.variantCardsEnabled !== 'true') return;
    spreadCards(grid);
    countAllVariantCards(grid);

    if (observedGrid === grid) return;
    observedGrid = grid;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(() => requestAnimationFrame(initialise)).observe(container, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', initialise);
  document.addEventListener('shopify:section:load', initialise);
})();
